import { ChatMessage } from "src/pubnub";
import { userAPIUrls, chatAPIUrls } from "../common/apiActions";
import { fetch as http } from "./http";
import { apiBaseUrl } from "./http";
import { store } from "../stores";
import moment from "moment-timezone";

// ─── Helper: get auth token ───
const getAuthToken = (): string => store?.getState()?.authUser?.token ?? "";

// ─── Helper: get timezone ───
const getTimezone = (): string => moment.tz.guess() ?? "";

// ─── Custom multipart POST for chat messages (supports files[] as array) ───
const postMessageWithFiles = async (
  url: string,
  payload: Record<string, any>,
  files: File[] = []
): Promise<any> => {
  const formData = new FormData();

  // Append all text/scalar fields
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (typeof value === "object" && !(value instanceof File)) {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, String(value));
    }
  });

  // Append files as "files[]" so the backend receives a proper array
  files.forEach((file) => {
    formData.append("files[]", file, file.name);
  });

  const response = await fetch(apiBaseUrl + url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getAuthToken()}`,
      timezone: getTimezone(),
      // NOTE: Do NOT set Content-Type — browser sets it with boundary automatically
    },
    body: formData,
  });

  if (response.status === 200) {
    return response.json();
  }

  // Handle errors consistently
  if (response.status === 401 || response.status === 403) {
    throw new Error("You don't have permission, contact administrator");
  }
  if (response.status === 422) {
    const errors = await response.json();
    const message = typeof errors === "object"
      ? Object.values(errors).join(", ")
      : "Please fill all required fields";
    throw new Error(message);
  }
  throw new Error("Something went wrong");
};

// Fetch chat users with pagination and search
export const fetchChatUsersService = async (page: number = 1, search: string = "") => {
  try {
    let url = `${chatAPIUrls?.getChatUsers}?page=${page}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    const data = await http.Get(url);
    return data;
  } catch (error: any) {
    throw error;
  }
};

// Create a chat room (private 1:1 or group)
export const createChatRoomService = async (payload: {
  channel_id: string;
  chat_type: "private" | "group";
  group_name: string | null;
  user_ids: number[];
}) => {
  try {
    const data = await http.Post(chatAPIUrls?.createChatRoom, payload);
    return data;
  } catch (error: any) {
    throw error;
  }
};

export const pubnubTokenService = async (channelId?: string) => {
  try {
    const url = channelId
      ? `${chatAPIUrls?.pubNubToken}?channel_id=${encodeURIComponent(channelId)}`
      : chatAPIUrls?.pubNubToken;
    const data = await http.Get(url);
    return data?.token;
  } catch (error: any) {
    throw error;
  }
};

// Mark chat as read by channel ID
export const readChatService = async (channelId: string) => {
  try {
    const data = await http.Get(`${chatAPIUrls?.readChat}/${channelId}`);
    return data;
  } catch (error: any) {
    throw error;
  }
};

// Update a group (rename, add/remove members)
export const updateGroupService = async (
  channelId: string,
  payload: {
    group_name?: string;
    user_ids?: number[];
    action?: "add" | "remove";
  }
) => {
  try {
    const data = await http.Post(`${chatAPIUrls?.updateGroup}/${channelId}`, payload);
    return data;
  } catch (error: any) {
    throw error;
  }
};

// Send a chat message with files sent as a proper array
export const sendMessageService = async (payload: ChatMessage, files?: File[]) => {
  const messagePayload: Record<string, any> = {
    type: payload.type,
    channel_id: payload.channelId,
    timestamp: payload.timestamp.toString(),
    text: payload.text || "",
    status: "sending",
  };

  // Include replyTo if present
  if (payload.replyTo) {
    messagePayload.replyTo = payload.replyTo;
  }

  try {
    const data = await postMessageWithFiles(
      chatAPIUrls?.sendChatMessage,
      messagePayload,
      files ?? []
    );
    return data;
  } catch (error: any) {
    throw error;
  }
};



// Fetch all chat rooms (both private and group)
// export const fetchChatRoomsService = async (search:string, page: number) => {
//   try {
//     const data = await http.Get(chatAPIUrls?.getChatRooms);
//     return data;
//   } catch (error: any) {
//     throw error;
//   }
// };

export const fetchChatRoomsService = async (
  search: string = '',
  page: number = 1,
  chatType: 'private' | 'group' | 'ongoing' = 'private'
): Promise<any> => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      chat_type: chatType,
    });

    if (search?.trim()) {
      params.append('search', search.trim());
    }

    const url = `${chatAPIUrls?.getChatRooms}?${params.toString()}`;
    const response = await http.Get(url);

    // Return the full response structure
    return response;
  } catch (error: any) {
    console.error('Error fetching chat rooms:', error);
    throw error;
  }
};

export const fetchUsersService = async (
  { type = 0 }: { type: 0 | 1 } = { type: 0 },
) => {
  try {
    const data = await http.Get(`${userAPIUrls?.getAgents}?type=${type}`);
    return data;
  } catch (error: any) {
    throw error;
  }
};

export const fetchAllUsersService = async (
  { type = 0 }: { type: 0 | 1 } = { type: 0 },
) => {
  try {
    const data = await http.Get(`${userAPIUrls?.getUsers}?type=${type}`);
    return data;
  } catch (error: any) {
    throw error;
  }
};

export const fetchUsersAgentsService = async () => {
  try {
    const data = await http.Get(`${userAPIUrls?.getAgents}`);
    return data;
  } catch (error: any) {
    throw error;
  }
};

export const fetchUsersAgentManagersService = async () => {
  try {
    const data = await http.Get(`${userAPIUrls?.getAgentManagers}`);
    return data;
  } catch (error: any) {
    throw error;
  }
};

export const fetchLocationBaseAgentsService = async () => {
  try {
    const data = await http.Get(`${userAPIUrls?.getLocationBaseAgents}`);
    return data;
  } catch (error: any) {
    throw error;
  }
};

export const createUserService = async (payload: any) => {
  try {
    const data = await http.Post(userAPIUrls?.create, payload);
    return data;
  } catch (error: any) {
    payload?.callback && payload?.callback(true);
    delete payload?.callback;
    throw new Error(error);
  }
};

export const updateUserService = async (payload: any) => {
  try {
    const data = await http.Post(userAPIUrls?.update, payload);
    return data;
  } catch (error: any) {
    payload?.callback && payload?.callback(true);
    delete payload?.callback;
    throw new Error(error);
  }
};
